<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import ExcelJS from 'exceljs'
import { del, get, patch, post } from '@/api'
import { type Client, useClientStore } from '@/stores/client'
import { clientItemsApi, clientsApi, targetItemMapApi, targetsApi } from '@/api/entities'

definePage({
  meta: {
    title: '매핑 관리',
    action: 'read',
    subject: 'DriverConfig',
  },
})

// 스토어
const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 고객사 목록
const clients = ref<Client[]>([])
const clientsLoading = ref(false)
const clientSearchQuery = ref('')

// 고객사 목록 로드
const loadClients = async () => {
  clientsLoading.value = true
  try {
    const response = await clientsApi.listClients({
      per_page: 10000,
      sort: 'name',
    })

    if (response.success && response.data) {
      // API 응답이 { data: Client[] } 또는 Client[] 형태일 수 있음
      const responseData = response.data as { data?: Client[] } | Client[]

      clients.value = Array.isArray(responseData) ? responseData : (responseData.data || [])
    }
  }
  catch (error) {
    console.error('클라이언트 목록 로드 실패:', error)
  }
  finally {
    clientsLoading.value = false
  }
}

// 필터링된 클라이언트 목록
const filteredClients = computed(() => {
  if (!clientSearchQuery.value)
    return clients.value

  const query = clientSearchQuery.value.toLowerCase()

  return clients.value.filter(client =>
    client.name?.toLowerCase().includes(query),
  )
})

// 고객사 선택 핸들러
const onClientChange = (client: Client | null) => {
  clientStore.updateSelectedClient(client)
}

// 인터페이스
interface SortItem {
  key: string
  order?: 'asc' | 'desc'
}

interface DriverConfig {
  id: number
  name: string
  download_driver: {
    id: number
    name: string
  }
  collected_review_count: number | null
  filtered_review_count: number | null
}

interface TargetItemMap {
  id: number
  config_id: number
  client_id: number
  driver_name: string
  target_item_code: string
  target_item_name: string | null
  target_item_name_resolved: number | null
  target_item_url: string | null
  client_item_code: string
  client_item_name: string | null
  client_item_name_resolved: number | null
  client_item_url: string | null
  similarity: number | null
  collected_review_count: number | null
  filtered_review_count: number | null
  duplicate_client_items: number | null
  duplicate_target_items: number | null
}

// 상태
const loading = ref(false)
const mappings = ref<TargetItemMap[]>([])
const totalMapCount = ref(0)
const page = ref(1)
const itemsPerPage = ref(25)
const search = ref('')
const sortBy = ref<SortItem[]>([{ key: 'id', order: 'desc' }])
const selectedMappings = ref<TargetItemMap[]>([])
const driverConfigs = ref<DriverConfig[]>([])
const selectedDriverConfigId = ref<number | null>(null)

// 통계
const clientCollectedReviewsCount = ref<number | null>(null)
const clientFilteredReviewsCount = ref<number | null>(null)

// 스낵바
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 엑셀 다운로드 로딩
const isExcelDownloading = ref(false)

// 다이얼로그
const confirmDialog = ref(false)
const confirmMessage = ref('')
const confirmAction = ref<(() => void) | null>(null)

// 매핑 수정 다이얼로그
const editDialog = ref(false)

const mappingForEdit = reactive({
  id: null as number | null,
  target_item_code: '',
  client_item_code: '',
})

// 매핑 추가 다이얼로그
const createDialog = ref(false)

const mappingsForCreate = ref([
  { config_id: null as number | null, target_item_code: '', client_item_code: '' },
])

// 테이블 헤더
const headers = [
  { title: 'ID', key: 'id', sortable: true, width: '80px' },
  { title: '채널', key: 'driver_name', sortable: true },
  { title: '채널 상품', key: 'target_item_name' },
  { title: '자사몰 상품', key: 'client_item_name' },
  { title: '유사도', key: 'similarity', sortable: true, width: '100px' },
  { title: '수집된 리뷰', key: 'collected_review_count', sortable: true, width: '120px' },
  { title: '이관 예상', key: 'filtered_review_count', sortable: true, width: '120px' },
  { title: '자사몰 중복', key: 'duplicate_client_items', sortable: true, width: '100px' },
  { title: '채널 중복', key: 'duplicate_target_items', sortable: true, width: '100px' },
  { title: '', key: 'actions', sortable: false, width: '60px' },
]

// 드라이버 설정 옵션
const driverConfigOptions = computed(() => {
  const options: { title: string; value: number | null }[] = [{ title: '전체 채널', value: null }]

  driverConfigs.value.forEach(config => {
    options.push({
      title: config.download_driver?.name || config.name,
      value: config.id,
    })
  })

  return options
})

// 추가 가능한 채널 목록
const addableChannels = computed(() => {
  return driverConfigs.value.map(config => ({
    title: config.download_driver?.name || config.name,
    value: config.id,
  }))
})

// 채널별 리뷰 수
const reviewCountByChannels = computed(() => {
  return driverConfigs.value.map(config => ({
    channel_name: config.download_driver?.name || config.name,
    collected_review_count: config.collected_review_count,
    filtered_review_count: config.filtered_review_count,
  }))
})

// 숫자 포맷
const formatNumber = (num: number | null) => {
  if (num === null || num === undefined)
    return '-'

  return num.toLocaleString()
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 확인 다이얼로그
const showConfirm = (message: string, action: () => void) => {
  confirmMessage.value = message
  confirmAction.value = action
  confirmDialog.value = true
}

const executeConfirmAction = () => {
  if (confirmAction.value)
    confirmAction.value()

  confirmDialog.value = false
}

// 드라이버 설정 로드
const loadDriverConfigs = async () => {
  if (!selectedClient.value?.id) {
    driverConfigs.value = []

    return
  }

  try {
    const { success, data } = await get<{ data: DriverConfig[] }>(
      `/review/clients/${selectedClient.value.id}/driver-configs`,
      { per_page: 100 },
    )

    if (success && data)
      driverConfigs.value = data.data || []
  }
  catch (error) {
    console.error('Failed to load driver configs:', error)
  }
}

// 고객사 통계 응답 타입
interface ClientStats {
  collected_review_count: number | null
  filtered_review_count: number | null
}

// 고객사 통계 로드
const loadClientStats = async () => {
  if (!selectedClient.value?.id) {
    clientCollectedReviewsCount.value = null
    clientFilteredReviewsCount.value = null

    return
  }

  try {
    const { success, data } = await get<ClientStats>(`/review/clients/${selectedClient.value.id}`)
    if (success && data) {
      clientCollectedReviewsCount.value = data.collected_review_count
      clientFilteredReviewsCount.value = data.filtered_review_count
    }
  }
  catch (error) {
    console.error('Failed to load client stats:', error)
  }
}

// 매핑 목록 로드
const loadMappings = async () => {
  if (!selectedClient.value?.id) {
    mappings.value = []
    totalMapCount.value = 0

    return
  }

  loading.value = true
  try {
    const sortKey = sortBy.value[0]?.key || 'id'
    const sortOrder = sortBy.value[0]?.order || 'desc'

    const params: Record<string, string | number> = {
      page: page.value,
      per_page: itemsPerPage.value,
      sort: sortKey,
      order: sortOrder,
    }

    if (search.value)
      params.search = search.value

    if (selectedDriverConfigId.value)
      params.driver_config_id = selectedDriverConfigId.value

    const { success, data } = await get<{ data: TargetItemMap[]; total: number }>(
      `/review/clients/${selectedClient.value.id}/target-item-map`,
      params,
    )

    if (success && data) {
      mappings.value = data.data || []
      totalMapCount.value = data.total || 0
    }
  }
  catch (error) {
    console.error('Failed to load mappings:', error)
    showNotification('매핑 목록을 불러오는데 실패했습니다.', 'error')
  }
  finally {
    loading.value = false
  }
}

// 매핑 삭제
const deleteMapping = (mapping: TargetItemMap) => {
  showConfirm(`${mapping.id} 매핑을 삭제하시겠습니까?`, async () => {
    try {
      const { success } = await del(
        `/review-client/${mapping.client_id}/driver-config/${mapping.config_id}/review-target-item-map/${mapping.id}`,
      )

      if (success) {
        showNotification('매핑을 삭제했습니다.')
        await loadMappings()
        await loadClientStats()
        await loadDriverConfigs()
      }
      else {
        showNotification('매핑 삭제에 실패했습니다.', 'error')
      }
    }
    catch (error) {
      console.error('Failed to delete mapping:', error)
      showNotification('매핑 삭제에 실패했습니다.', 'error')
    }
  })
}

// 매핑 수정 다이얼로그 열기
const openEditDialog = (mapping: TargetItemMap) => {
  mappingForEdit.id = mapping.id
  mappingForEdit.target_item_code = mapping.target_item_code
  mappingForEdit.client_item_code = mapping.client_item_code
  editDialog.value = true
}

// 매핑 수정
const updateMapping = async () => {
  if (!selectedClient.value?.id || !mappingForEdit.id)
    return

  try {
    const { success } = await patch(
      `/review/clients/${selectedClient.value.id}/target-item-map/${mappingForEdit.id}`,
      {
        target_item_code: mappingForEdit.target_item_code,
        client_item_code: mappingForEdit.client_item_code,
      },
    )

    if (success) {
      showNotification('매핑을 수정했습니다.')
      editDialog.value = false
      await loadMappings()
      await loadClientStats()
      await loadDriverConfigs()
    }
    else {
      showNotification('매핑 수정에 실패했습니다. 중복된 값인지 확인해주세요.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to update mapping:', error)
    showNotification('매핑 수정에 실패했습니다.', 'error')
  }
}

// 매핑 추가 다이얼로그 열기
const openCreateDialog = () => {
  mappingsForCreate.value = [
    { config_id: null, target_item_code: '', client_item_code: '' },
  ]
  createDialog.value = true
}

// 매핑 추가 행 추가
const addCreateMappingInput = () => {
  const lastMapping = mappingsForCreate.value[mappingsForCreate.value.length - 1]

  mappingsForCreate.value.push({
    config_id: lastMapping.config_id,
    target_item_code: '',
    client_item_code: '',
  })
}

// 매핑 추가 행 제거
const removeCreateMappingInput = (index: number) => {
  if (mappingsForCreate.value.length > 1)
    mappingsForCreate.value.splice(index, 1)
}

// 매핑 저장
const storeMapping = async () => {
  if (!selectedClient.value?.id)
    return

  const validMappings = mappingsForCreate.value.filter(
    m => m.config_id && m.target_item_code && m.client_item_code,
  )

  if (validMappings.length === 0) {
    showNotification('유효한 매핑 정보를 입력해주세요.', 'warning')

    return
  }

  try {
    const { success } = await post(
      `/review/clients/${selectedClient.value.id}/target-item-map`,
      { mappings: validMappings },
    )

    if (success) {
      showNotification('매핑을 추가했습니다.')
      createDialog.value = false
      await loadMappings()
      await loadClientStats()
      await loadDriverConfigs()
    }
    else {
      showNotification('매핑 추가에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to store mapping:', error)
    showNotification('매핑 추가에 실패했습니다.', 'error')
  }
}

// 전체 매핑 삭제
const deleteAllMappings = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm(`${selectedClient.value.name}의 모든 매핑을 삭제하시겠습니까?`, async () => {
    try {
      const { success } = await del(
        `/review/clients/${selectedClient.value!.id}/target-item-map`,
      )

      if (success) {
        showNotification('모든 매핑을 삭제했습니다.')
        await loadMappings()
        await loadClientStats()
        await loadDriverConfigs()
      }
      else {
        showNotification('매핑 삭제에 실패했습니다.', 'error')
      }
    }
    catch (error) {
      console.error('Failed to delete all mappings:', error)
      showNotification('매핑 삭제에 실패했습니다.', 'error')
    }
  })
}

// 상품명 수집 - 전체 자사몰
const resolveClientProductNames = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('전체 매핑의 자사몰 상품명 수집을 요청하시겠습니까?', async () => {
    try {
      const { success } = await clientItemsApi.resolveProductNames(selectedClient.value!.id)
      if (success)
        showNotification('자사몰 상품명 수집이 요청되었습니다. 완료 시 슬랙으로 알려드립니다.')
      else
        showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to resolve client product names:', error)
      showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 상품명 수집 - 전체 채널
const resolveTargetProductNames = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('전체 매핑의 채널 상품명 수집을 요청하시겠습니까?', async () => {
    try {
      const { success } = await targetsApi.resolveProductNames(selectedClient.value!.id)
      if (success)
        showNotification('채널 상품명 수집이 요청되었습니다. 완료 시 슬랙으로 알려드립니다.')
      else
        showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to resolve target product names:', error)
      showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 상품명 수집 - 선택된 매핑 자사몰
const resolveClientProductNamesForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  showConfirm('선택된 매핑의 자사몰 상품명 수집을 요청하시겠습니까?', async () => {
    try {
      const mappingIds = selectedMappings.value.map(m => m.id)

      const { success } = await clientItemsApi.resolveProductNamesByTargetItemMapIds(
        selectedClient.value!.id,
        mappingIds,
      )

      if (success)
        showNotification('선택된 매핑의 자사몰 상품명 수집이 요청되었습니다.')
      else
        showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to resolve client product names:', error)
      showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 상품명 수집 - 선택된 매핑 채널
const resolveTargetProductNamesForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  showConfirm('선택된 매핑의 채널 상품명 수집을 요청하시겠습니까?', async () => {
    try {
      const mappingIds = selectedMappings.value.map(m => m.id)

      const { success } = await targetsApi.resolveProductNamesByTargetItemMapIds(
        selectedClient.value!.id,
        mappingIds,
      )

      if (success)
        showNotification('선택된 매핑의 채널 상품명 수집이 요청되었습니다.')
      else
        showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to resolve target product names:', error)
      showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 상품명 수집 - 특정 채널
const resolveTargetProductNameByDriverConfigId = (configId: number, driverName: string) => {
  if (!selectedClient.value?.id)
    return

  showConfirm(`${driverName}의 상품명 수집을 요청하시겠습니까?`, async () => {
    try {
      const { success } = await targetsApi.resolveProductNameByDriverConfigId(
        selectedClient.value!.id,
        configId,
      )

      if (success)
        showNotification(`${driverName}의 상품명 수집이 요청되었습니다.`)
      else
        showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to resolve target product names:', error)
      showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 상품명 수집 - 단일 매핑 (채널)
const resolveTargetProductNameById = (mappingId: number) => {
  if (!selectedClient.value?.id)
    return

  showConfirm(`채널 상품 코드 ${mappingId}의 상품명 수집을 요청하시겠습니까?`, async () => {
    try {
      const { success } = await targetsApi.resolveProductNamesByTargetItemMapIds(
        selectedClient.value!.id,
        [mappingId],
      )

      if (success)
        showNotification(`채널 상품 코드 ${mappingId}의 상품명 수집이 요청되었습니다.`)
      else
        showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to resolve target product name:', error)
      showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 상품명 수집 - 단일 매핑 (자사몰)
const resolveClientProductNameById = (mappingId: number) => {
  if (!selectedClient.value?.id)
    return

  showConfirm(`자사몰 상품 코드 ${mappingId}의 상품명 수집을 요청하시겠습니까?`, async () => {
    try {
      const { success } = await clientItemsApi.resolveProductNamesByTargetItemMapIds(
        selectedClient.value!.id,
        [mappingId],
      )

      if (success)
        showNotification(`자사몰 상품 코드 ${mappingId}의 상품명 수집이 요청되었습니다.`)
      else
        showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to resolve client product name:', error)
      showNotification('상품명 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 유사도 생성 - 전체
const buildSimilarity = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('전체 매핑의 유사도 계산을 요청하시겠습니까?', async () => {
    try {
      const { success } = await targetItemMapApi.buildSimilarity(selectedClient.value!.id)
      if (success) {
        showNotification('유사도 계산이 요청되었습니다. 완료 시 슬랙으로 알려드립니다.')
        await loadMappings()
      }
      else {
        showNotification('유사도 계산 요청에 실패했습니다.', 'error')
      }
    }
    catch (error) {
      console.error('Failed to build similarity:', error)
      showNotification('유사도 계산 요청에 실패했습니다.', 'error')
    }
  })
}

// 유사도 생성 - 선택된 매핑
const buildSimilarityForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  showConfirm('선택된 매핑의 유사도 계산을 요청하시겠습니까?', async () => {
    try {
      const mappingIds = selectedMappings.value.map(m => m.id)

      const { success } = await targetItemMapApi.buildSimilarityForMappings(
        selectedClient.value!.id,
        mappingIds,
      )

      if (success)
        showNotification('선택된 매핑의 유사도 계산이 요청되었습니다.')
      else
        showNotification('유사도 계산 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to build similarity:', error)
      showNotification('유사도 계산 요청에 실패했습니다.', 'error')
    }
  })
}

// 리뷰 수집 응답 타입
interface CollectReviewsResponse {
  is_reserved?: boolean
}

// 리뷰 수집 - 전체
const collectReviews = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('전체 매핑의 리뷰 수집을 시작하시겠습니까?', async () => {
    try {
      const { success, data } = await clientsApi.collectReviews(selectedClient.value!.id)
      if (success) {
        const responseData = data as CollectReviewsResponse | undefined
        if (responseData?.is_reserved)
          showNotification('리뷰 수집이 시작되었습니다. 완료 시 슬랙으로 알려드립니다.')
        else
          showNotification('동일한 요청이 이미 존재합니다.', 'warning')
      }
      else {
        showNotification('리뷰 수집 요청에 실패했습니다.', 'error')
      }
    }
    catch (error) {
      console.error('Failed to collect reviews:', error)
      showNotification('리뷰 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 리뷰 수집 - 선택된 매핑
const collectReviewsForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  showConfirm('선택된 매핑의 리뷰 수집을 시작하시겠습니까?', async () => {
    try {
      const mappingIds = selectedMappings.value.map(m => m.id)

      const { success } = await clientsApi.collectReviewsForMappings(
        selectedClient.value!.id,
        mappingIds,
      )

      if (success)
        showNotification('선택된 매핑의 리뷰 수집이 시작되었습니다.')
      else
        showNotification('리뷰 수집 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to collect reviews:', error)
      showNotification('리뷰 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 리뷰 수집 - 단일 매핑
const collectReviewsForMapping = (mappingId: number) => {
  if (!selectedClient.value?.id)
    return

  showConfirm('해당 매핑의 리뷰 수집을 시작하시겠습니까?', async () => {
    try {
      const { success } = await clientsApi.collectReviewsForMappings(
        selectedClient.value!.id,
        [mappingId],
      )

      if (success)
        showNotification('리뷰 수집이 시작되었습니다.')
      else
        showNotification('리뷰 수집 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to collect reviews:', error)
      showNotification('리뷰 수집 요청에 실패했습니다.', 'error')
    }
  })
}

// 리뷰 수 계산
const updateExpectedReviewCount = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('리뷰 수 계산을 요청하시겠습니까?', async () => {
    try {
      const { success } = await clientsApi.updateExpectedReviewCount(selectedClient.value!.id)
      if (success)
        showNotification('리뷰 수 계산이 요청되었습니다. 완료 시 슬랙으로 알려드립니다.')
      else
        showNotification('리뷰 수 계산 요청에 실패했습니다.', 'error')
    }
    catch (error) {
      console.error('Failed to update expected review count:', error)
      showNotification('리뷰 수 계산 요청에 실패했습니다.', 'error')
    }
  })
}

// 엑셀 다운로드
const downloadExcelExport = async () => {
  if (!selectedClient.value?.id)
    return

  isExcelDownloading.value = true
  try {
    const params: Record<string, string | number> = {
      page: 1,
      per_page: 100000,
      sort: 'id',
      order: 'asc',
    }

    if (selectedDriverConfigId.value)
      params.driver_config_id = selectedDriverConfigId.value

    const { success, data } = await get<{ data: TargetItemMap[] }>(
      `/review/clients/${selectedClient.value.id}/target-item-map`,
      params,
    )

    if (success && data) {
      const workbook = new ExcelJS.Workbook()

      const excelColumns = [
        { header: 'ID', key: 'id' },
        { header: '채널', key: 'driver_name' },
        { header: '채널 상품명', key: 'target_item_name' },
        { header: '채널 상품 코드', key: 'target_item_code' },
        { header: '자사몰 상품명', key: 'client_item_name' },
        { header: '자사몰 상품 코드', key: 'client_item_code' },
        { header: '유사도', key: 'similarity' },
        { header: '수집된 리뷰 수', key: 'collected_review_count' },
        { header: '이관 대상 리뷰 수', key: 'filtered_review_count' },
        { header: '자사몰 중복 건수', key: 'duplicate_client_items' },
        { header: '채널 중복 건수', key: 'duplicate_target_items' },
      ]

      driverConfigs.value.forEach(driverConfig => {
        if (selectedDriverConfigId.value && selectedDriverConfigId.value !== driverConfig.id)
          return

        const worksheet = workbook.addWorksheet(driverConfig.download_driver?.name || driverConfig.name)

        worksheet.columns = excelColumns

        const filteredData = data.data.filter(item => item.config_id === driverConfig.id)

        filteredData.forEach(row => {
          worksheet.addRow(row)
        })
      })

      const buffer = await workbook.xlsx.writeBuffer()

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = `${selectedClient.value.name}.xlsx`
      link.click()
      window.URL.revokeObjectURL(url)
    }
  }
  catch (error) {
    console.error('Failed to download excel:', error)
    showNotification('엑셀 다운로드에 실패했습니다.', 'error')
  }
  finally {
    isExcelDownloading.value = false
  }
}

// 페이지네이션 핸들러
const handlePageChange = (newPage: number) => {
  page.value = newPage
  loadMappings()
}

// 정렬 핸들러
const handleSortChange = (newSortBy: SortItem[]) => {
  sortBy.value = newSortBy
  loadMappings()
}

// 초기 로드
onMounted(() => {
  loadClients()
})

// 고객사 변경 감시
watch(selectedClient, async () => {
  page.value = 1
  selectedMappings.value = []
  selectedDriverConfigId.value = null
  driverConfigs.value = []
  await Promise.all([
    loadDriverConfigs(),
    loadClientStats(),
    loadMappings(),
  ])
}, { immediate: true })

// 채널 필터 변경 감시
watch(selectedDriverConfigId, () => {
  page.value = 1
  loadMappings()
})

// 검색 디바운스
let searchTimeout: ReturnType<typeof setTimeout> | null = null
watch(search, () => {
  if (searchTimeout)
    clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    page.value = 1
    loadMappings()
  }, 500)
})
</script>

<template>
  <VRow>
    <!-- 고객사 선택 및 통계 -->
    <VCol
      cols="12"
      md="4"
    >
      <VCard>
        <VCardText>
          <VAutocomplete
            :model-value="selectedClient"
            :items="filteredClients"
            :search="clientSearchQuery"
            item-title="name"
            item-value="id"
            return-object
            label="고객사 선택"
            placeholder="고객사를 선택하세요"
            :loading="clientsLoading"
            clearable
            no-filter
            @update:model-value="onClientChange"
            @update:search="clientSearchQuery = $event"
          />
        </VCardText>
      </VCard>
    </VCol>

    <VCol
      cols="12"
      md="4"
    >
      <VCard>
        <VCardText>
          <VRow>
            <VCol cols="6">
              <div class="d-flex align-center">
                <VAvatar
                  color="info"
                  variant="tonal"
                  size="48"
                  class="me-3"
                >
                  <VIcon
                    icon="tabler-layers-intersect"
                    size="24"
                  />
                </VAvatar>
                <div>
                  <VChip
                    v-if="clientCollectedReviewsCount === null"
                    size="small"
                    color="secondary"
                  >
                    계산 필요
                  </VChip>
                  <h4
                    v-else
                    class="text-h6 font-weight-bold mb-0"
                  >
                    {{ formatNumber(clientCollectedReviewsCount) }}
                  </h4>
                  <span class="text-body-2 text-disabled">수집된 리뷰 수</span>
                </div>
              </div>
            </VCol>
            <VCol cols="6">
              <div class="d-flex align-center">
                <VAvatar
                  color="success"
                  variant="tonal"
                  size="48"
                  class="me-3"
                >
                  <VIcon
                    icon="tabler-transfer"
                    size="24"
                  />
                </VAvatar>
                <div>
                  <VChip
                    v-if="clientFilteredReviewsCount === null"
                    size="small"
                    color="secondary"
                  >
                    계산 필요
                  </VChip>
                  <h4
                    v-else
                    class="text-h6 font-weight-bold mb-0"
                  >
                    {{ formatNumber(clientFilteredReviewsCount) }}
                  </h4>
                  <span class="text-body-2 text-disabled">이관 예상 리뷰 수</span>
                </div>
              </div>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>

    <VCol
      cols="12"
      md="4"
    >
      <VCard title="채널 별 리뷰 수">
        <VCardText class="pa-0">
          <VTable density="compact">
            <thead>
              <tr>
                <th>채널</th>
                <th>수집된 리뷰</th>
                <th>이관 예상</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="channel in reviewCountByChannels"
                :key="channel.channel_name"
              >
                <td>{{ channel.channel_name }}</td>
                <td>
                  <VChip
                    v-if="channel.collected_review_count === null"
                    size="x-small"
                    color="secondary"
                  >
                    계산 필요
                  </VChip>
                  <span v-else>{{ formatNumber(channel.collected_review_count) }}</span>
                </td>
                <td>
                  <VChip
                    v-if="channel.filtered_review_count === null"
                    size="x-small"
                    color="secondary"
                  >
                    계산 필요
                  </VChip>
                  <span v-else>{{ formatNumber(channel.filtered_review_count) }}</span>
                </td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 매핑 목록 -->
    <VCol cols="12">
      <VCard>
        <VCardText>
          <!-- 툴바 -->
          <VRow class="mb-4">
            <VCol
              cols="12"
              md="2"
            >
              <VTextField
                v-model="search"
                placeholder="상품코드 검색"
                density="compact"
                hide-details
                prepend-inner-icon="tabler-search"
              />
            </VCol>
            <VCol
              cols="12"
              md="7"
            >
              <div class="d-flex flex-wrap gap-2">
                <!-- 상품명 수집 -->
                <VMenu>
                  <template #activator="{ props }">
                    <VBtn
                      v-bind="props"
                      variant="outlined"
                      color="primary"
                      :disabled="driverConfigs.length === 0"
                    >
                      상품명 수집
                      <VIcon
                        end
                        icon="tabler-chevron-down"
                      />
                    </VBtn>
                  </template>
                  <VList>
                    <VListItem @click="resolveClientProductNames">
                      전체 매핑 자사몰 상품명 수집
                    </VListItem>
                    <VListItem @click="resolveTargetProductNames">
                      전체 매핑 채널 상품명 수집
                    </VListItem>
                    <VDivider />
                    <VListItem
                      :disabled="selectedMappings.length === 0"
                      @click="resolveClientProductNamesForMappings"
                    >
                      선택된 매핑({{ selectedMappings.length }}) 자사몰 상품명 수집
                    </VListItem>
                    <VListItem
                      :disabled="selectedMappings.length === 0"
                      @click="resolveTargetProductNamesForMappings"
                    >
                      선택된 매핑({{ selectedMappings.length }}) 채널 상품명 수집
                    </VListItem>
                    <VDivider />
                    <VListItem
                      v-for="config in driverConfigs"
                      :key="config.id"
                      @click="resolveTargetProductNameByDriverConfigId(config.id, config.download_driver?.name || config.name)"
                    >
                      <span class="text-primary">{{ config.download_driver?.name || config.name }}</span> 상품명 수집
                    </VListItem>
                  </VList>
                </VMenu>

                <!-- 유사도 생성 -->
                <VMenu>
                  <template #activator="{ props }">
                    <VBtn
                      v-bind="props"
                      variant="outlined"
                      color="primary"
                    >
                      유사도 생성
                      <VIcon
                        end
                        icon="tabler-chevron-down"
                      />
                    </VBtn>
                  </template>
                  <VList>
                    <VListItem @click="buildSimilarity">
                      전체 매핑 유사도 생성
                    </VListItem>
                    <VDivider />
                    <VListItem
                      :disabled="selectedMappings.length === 0"
                      @click="buildSimilarityForMappings"
                    >
                      선택된 매핑({{ selectedMappings.length }}) 유사도 생성
                    </VListItem>
                  </VList>
                </VMenu>

                <!-- 리뷰 수집 -->
                <VMenu>
                  <template #activator="{ props }">
                    <VBtn
                      v-bind="props"
                      variant="outlined"
                      color="primary"
                    >
                      리뷰 수집
                      <VIcon
                        end
                        icon="tabler-chevron-down"
                      />
                    </VBtn>
                  </template>
                  <VList>
                    <VListItem @click="collectReviews">
                      전체 매핑 리뷰 수집
                    </VListItem>
                    <VDivider />
                    <VListItem
                      :disabled="selectedMappings.length === 0"
                      @click="collectReviewsForMappings"
                    >
                      선택된 매핑({{ selectedMappings.length }}) 리뷰 수집
                    </VListItem>
                  </VList>
                </VMenu>

                <VBtn
                  variant="outlined"
                  color="primary"
                  @click="openCreateDialog"
                >
                  매핑 데이터 추가
                </VBtn>

                <VBtn
                  color="primary"
                  :loading="isExcelDownloading"
                  @click="downloadExcelExport"
                >
                  엑셀 다운로드
                </VBtn>

                <VBtn
                  variant="outlined"
                  color="error"
                  :disabled="!selectedClient"
                  @click="deleteAllMappings"
                >
                  업체 매핑 정보 삭제
                </VBtn>

                <VBtn
                  variant="outlined"
                  color="info"
                  :disabled="!selectedClient"
                  @click="updateExpectedReviewCount"
                >
                  리뷰 수 계산
                </VBtn>
              </div>
            </VCol>
            <VCol
              cols="12"
              md="3"
              class="d-flex justify-end align-center gap-3"
            >
              <span class="text-body-2">총 {{ formatNumber(totalMapCount) }}개 제품</span>
              <VSelect
                v-model="selectedDriverConfigId"
                :items="driverConfigOptions"
                item-title="title"
                item-value="value"
                density="compact"
                hide-details
                style="max-width: 200px;"
              />
            </VCol>
          </VRow>

          <!-- 데이터 테이블 -->
          <VDataTableServer
            v-model="selectedMappings"
            v-model:items-per-page="itemsPerPage"
            v-model:page="page"
            v-model:sort-by="sortBy"
            :headers="headers"
            :items="mappings"
            :items-length="totalMapCount"
            :loading="loading"
            show-select
            item-value="id"
            density="compact"
            class="text-no-wrap"
            @update:page="handlePageChange"
            @update:sort-by="handleSortChange"
          >
            <template #item.target_item_name="{ item }">
              <div>
                <span v-if="item.target_item_name_resolved === 1">
                  <strong>{{ item.target_item_name }}</strong>
                </span>
                <VChip
                  v-else-if="item.target_item_name_resolved === null"
                  size="x-small"
                  color="secondary"
                >
                  상품명 수집 전
                </VChip>
                <VChip
                  v-else
                  size="x-small"
                  color="error"
                >
                  수집 오류
                </VChip>
              </div>
              <div>
                <a
                  v-if="item.target_item_url"
                  :href="item.target_item_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary"
                >
                  {{ item.target_item_code }}
                </a>
                <span
                  v-else
                  class="text-body-2"
                >{{ item.target_item_code }}</span>
              </div>
            </template>

            <template #item.client_item_name="{ item }">
              <div>
                <span v-if="item.client_item_name_resolved === 1">
                  <strong>{{ item.client_item_name }}</strong>
                </span>
                <VChip
                  v-else-if="item.client_item_name_resolved === null"
                  size="x-small"
                  color="secondary"
                >
                  상품명 수집 전
                </VChip>
                <VChip
                  v-else
                  size="x-small"
                  color="error"
                >
                  수집 오류
                </VChip>
              </div>
              <div>
                <a
                  v-if="item.client_item_url"
                  :href="item.client_item_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary"
                >
                  {{ item.client_item_code }}
                </a>
                <span
                  v-else
                  class="text-body-2"
                >{{ item.client_item_code }}</span>
              </div>
            </template>

            <template #item.similarity="{ item }">
              <VChip
                v-if="item.similarity === null"
                size="x-small"
                color="secondary"
              >
                생성 전
              </VChip>
              <span v-else>{{ formatNumber(item.similarity) }}</span>
            </template>

            <template #item.collected_review_count="{ item }">
              <VChip
                v-if="item.collected_review_count === null"
                size="x-small"
                color="secondary"
              >
                수집 전
              </VChip>
              <span v-else>{{ formatNumber(item.collected_review_count) }}</span>
            </template>

            <template #item.filtered_review_count="{ item }">
              <VChip
                v-if="item.filtered_review_count === null"
                size="x-small"
                color="secondary"
              >
                수집 전
              </VChip>
              <span v-else>{{ formatNumber(item.filtered_review_count) }}</span>
            </template>

            <template #item.duplicate_client_items="{ item }">
              {{ item.duplicate_client_items ?? '-' }}
            </template>

            <template #item.duplicate_target_items="{ item }">
              {{ item.duplicate_target_items ?? '-' }}
            </template>

            <template #item.actions="{ item }">
              <VMenu>
                <template #activator="{ props }">
                  <VBtn
                    v-bind="props"
                    icon
                    variant="text"
                    size="small"
                  >
                    <VIcon icon="tabler-dots-vertical" />
                  </VBtn>
                </template>
                <VList density="compact">
                  <VListItem @click="resolveTargetProductNameById(item.id)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-circle"
                        size="16"
                      />
                    </template>
                    채널 상품명 수집
                  </VListItem>
                  <VListItem @click="resolveClientProductNameById(item.id)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-triangle"
                        size="16"
                      />
                    </template>
                    자사몰 상품명 수집
                  </VListItem>
                  <VDivider />
                  <VListItem @click="collectReviewsForMapping(item.id)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-square"
                        size="16"
                      />
                    </template>
                    리뷰 수집
                  </VListItem>
                  <VDivider />
                  <VListItem @click="openEditDialog(item)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-edit"
                        size="16"
                      />
                    </template>
                    수정
                  </VListItem>
                  <VListItem @click="deleteMapping(item)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-trash"
                        size="16"
                      />
                    </template>
                    삭제
                  </VListItem>
                </VList>
              </VMenu>
            </template>
          </VDataTableServer>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 확인 다이얼로그 -->
    <VDialog
      v-model="confirmDialog"
      max-width="400"
    >
      <VCard>
        <VCardTitle>확인</VCardTitle>
        <VCardText>{{ confirmMessage }}</VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="confirmDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="executeConfirmAction"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 매핑 수정 다이얼로그 -->
    <VDialog
      v-model="editDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>매핑 수정</VCardTitle>
        <VCardText>
          <VTextField
            v-model="mappingForEdit.target_item_code"
            label="채널 아이템 코드"
            class="mb-4"
          />
          <VTextField
            v-model="mappingForEdit.client_item_code"
            label="자사몰 아이템 코드"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="editDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="updateMapping"
          >
            저장
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 매핑 추가 다이얼로그 -->
    <VDialog
      v-model="createDialog"
      max-width="800"
    >
      <VCard>
        <VCardTitle>매핑 추가</VCardTitle>
        <VCardText>
          <VRow class="mb-2">
            <VCol cols="4">
              <span class="text-body-2 font-weight-medium">채널</span>
            </VCol>
            <VCol>
              <span class="text-body-2 font-weight-medium">채널 아이템 코드</span>
            </VCol>
            <VCol>
              <span class="text-body-2 font-weight-medium">자사몰 아이템 코드</span>
            </VCol>
            <VCol cols="1" />
          </VRow>

          <VRow
            v-for="(mapping, index) in mappingsForCreate"
            :key="index"
            class="mb-2"
          >
            <VCol cols="4">
              <VSelect
                v-model="mapping.config_id"
                :items="addableChannels"
                item-title="title"
                item-value="value"
                density="compact"
                hide-details
                placeholder="채널 선택"
              />
            </VCol>
            <VCol>
              <VTextField
                v-model="mapping.target_item_code"
                density="compact"
                hide-details
                placeholder="채널 아이템 코드"
              />
            </VCol>
            <VCol>
              <VTextField
                v-model="mapping.client_item_code"
                density="compact"
                hide-details
                placeholder="자사몰 아이템 코드"
              />
            </VCol>
            <VCol cols="1">
              <VBtn
                icon
                variant="outlined"
                color="error"
                size="small"
                :disabled="index === 0"
                @click="removeCreateMappingInput(index)"
              >
                <VIcon icon="tabler-x" />
              </VBtn>
            </VCol>
          </VRow>

          <VRow>
            <VCol>
              <VBtn
                block
                variant="outlined"
                color="primary"
                @click="addCreateMappingInput"
              >
                <VIcon
                  icon="tabler-plus"
                  class="me-2"
                />
                행 추가
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="createDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="storeMapping"
          >
            저장
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
.v-data-table :deep(tbody tr) {
  cursor: pointer;
}
</style>
